import * as _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import OnboardingConstants from 'constants/OnboardingConstants';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import {
  ENTRY_TASKS,
  GETTING_STARTED_TASKS,
  OnboardingTask,
  SEQUENCE_TASKS,
  TaskStatus,
} from 'onboarding/OnboardingConstants';
import {formatBytes} from 'util/NameUtil';

const ENTRY_FORMATTED_BYTES = formatBytes(
  OnboardingConstants.SPACE_ADDITION_SIZES.ONBOARDING[OnboardingConstants.MODULES.ENTRY]
);

const SEQUENCE_FORMATTED_BYTES = formatBytes(
  OnboardingConstants.SPACE_ADDITION_SIZES.ONBOARDING[OnboardingConstants.MODULES.SEQUENCE]
);

const GETTING_STARTED_FORMATTED_BYTES = formatBytes(
  OnboardingConstants.SPACE_ADDITION_SIZES.ONBOARDING[OnboardingConstants.MODULES.GETTING_STARTED]
);

function makeDummyListItem(title: string, description: string): OnboardingTask {
  return {
    completed: true,
    key: 'dummy-list-item',
    description,
    title,
    countTowardCompletion: true,
  };
}

function makeCompletionListItem(completed: boolean, title: string): OnboardingTask {
  return {
    completed,
    key: 'finish-space',
    title,
    countTowardCompletion: false,
  };
}

export function makeEntryListItems(
  tasks: Array<TaskStatus>,
  isSampleEntry: boolean,
  completed: boolean,
  canEarnSpace: boolean
): Array<OnboardingTask> {
  const entryTaskKeys = Object.keys(ENTRY_TASKS);
  const listItems: Array<OnboardingTask> = tasks
    .filter((task: TaskStatus) => entryTaskKeys.includes(task.key))
    .map((task: TaskStatus) => ({
      key: task.key,
      completed: task.completed,
      title: ENTRY_TASKS[task.key].title,
      description: ENTRY_TASKS[task.key].description(isSampleEntry),
      countTowardCompletion: true,
    }));
  assertAndContinue(listItems.length === entryTaskKeys.length, 'Unexpected task keys');
  listItems.unshift(
    makeDummyListItem('Open a Notebook entry', 'Navigate to "Example Project" and open "Example Entry".')
  );
  if (canEarnSpace) {
    listItems.push(makeCompletionListItem(completed, `Earn ${ENTRY_FORMATTED_BYTES} of extra space!`));
  } else {
    listItems.push(makeCompletionListItem(completed, `Celebrate!`));
  }
  return listItems;
}

export function makeSequenceListItems(
  tasks: Array<TaskStatus>,
  completed: boolean,
  canEarnSpace: boolean
): Array<OnboardingTask> {
  const sequenceTaskKeys = Object.keys(SEQUENCE_TASKS);
  const listItems: Array<OnboardingTask> = tasks
    .filter((task: TaskStatus) => sequenceTaskKeys.includes(task.key))
    .map((task: TaskStatus) => ({
      key: task.key,
      completed: task.completed,
      steps: SEQUENCE_TASKS[task.key].steps,
      title: SEQUENCE_TASKS[task.key].title,
      tutorialLink: SEQUENCE_TASKS[task.key].tutorialLink,
      countTowardCompletion: true,
    }));
  assertAndContinue(listItems.length === sequenceTaskKeys.length, 'Unexpected task keys');
  listItems.unshift(
    makeDummyListItem('Open a sequence', 'Navigate to "Example Project" and open "pBR322_EGFR".')
  );
  if (canEarnSpace) {
    listItems.push(makeCompletionListItem(completed, `Earn ${SEQUENCE_FORMATTED_BYTES} of extra space!`));
  } else {
    listItems.push(makeCompletionListItem(completed, `Celebrate!`));
  }
  return listItems;
}

export function makeGettingStartedItems(
  serverTasks: Array<TaskStatus>,
  completed: boolean,
  canEarnSpace: boolean
): Array<OnboardingTask> {
  const serverTasksByKey = _.indexBy(serverTasks, 'key');
  const listItems: Array<OnboardingTask> = Array.from(GETTING_STARTED_TASKS)
    .filter(([key, task]) => serverTasksByKey[key])
    .map(([key, task]) => ({
      key,
      completed: serverTasksByKey[key].completed,
      title: task.title,
      onClick: task.onClick,
      resultForTracking: task.resultForTracking,
      countTowardCompletion: true,
    }));
  assertAndContinue(listItems.length === serverTasks.length, 'Unexpected task keys');
  if (canEarnSpace) {
    listItems.push(
      makeCompletionListItem(completed, `Earn ${GETTING_STARTED_FORMATTED_BYTES} of extra space!`)
    );
  } else {
    listItems.push(makeCompletionListItem(completed, `Celebrate!`));
  }
  return listItems;
}

export function trackChecklistOpened(checklist: string, source: string): void {
  analytics.track('onboarding_checklist', 'opened', {
    checklist,
    source,
  });
}

export function trackChecklistCollapsed(
  isCollapsed: boolean,
  checklistKey: string,
  checklistTitle: string
): void {
  const action = isCollapsed ? 'minimized' : 'expanded';
  analytics.track('onboarding_checklist', action, {
    checklist: checklistKey,
    checklistTitle,
    checklistReward: '1GB_earned',
    numberOfTasks: 5,
    eventLastModified: 'FY21_Q1a2',
  });
}

export function trackClickTask(
  taskNumber: number,
  taskInstructions: string,
  isOpen: boolean,
  checklistKey: string,
  checklistTitle: string,
  result?: string
): void {
  analytics.track('onboarding_task', 'clicked', {
    checklist: checklistKey,
    checklistTitle,
    checklistReward: '1GB_earned',
    numberOfTasks: 5,
    taskNumber,
    taskInstructions,
    result: (result ?? isOpen) ? 'detailed_instructions_expanded' : 'detailed_instructions_hidden',
    eventLastModified: 'FY21_Q1a2',
  });
}

export function shouldShowOnboardingChecklistsIcon(): boolean {
  const gettingStartedModule = HomeDashboardStore.getNUEModuleState(
    OnboardingConstants.MODULES.GETTING_STARTED
  );
  const entryModule = HomeDashboardStore.getNUEModuleState(OnboardingConstants.MODULES.ENTRY);
  const sequenceModule = HomeDashboardStore.getNUEModuleState(OnboardingConstants.MODULES.SEQUENCE);
  return Boolean(
    gettingStartedModule &&
      entryModule &&
      sequenceModule &&
      !(gettingStartedModule.completed && entryModule.completed && sequenceModule.completed)
  );
}
